/** shadcn/ui Button — Slate & Field Green theme. */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-45",
  {
    variants: {
      variant: {
        default:     "bg-accent text-accent-text font-semibold hover:bg-accent-hover active:bg-accent-press shadow-sm",
        destructive: "text-status-text-critical hover:bg-status-critical/10",
        outline:     "border border-line bg-surface text-ink hover:bg-surface-sunken",
        secondary:   "bg-surface-raised text-ink shadow-ring hover:bg-surface-sunken",
        ghost:       "text-ink-secondary hover:bg-surface-sunken hover:text-ink",
        link:        "text-accent-solid underline-offset-4 hover:underline",
        // Aliases for existing call-sites that use "primary" / "danger"
        primary:     "bg-accent-solid text-white font-semibold hover:bg-accent-deep active:bg-accent-text shadow-sm",
        danger:      "text-status-text-critical hover:bg-status-critical/10",
      },
      size: {
        default: "h-9 px-3.5",
        sm:      "h-7 px-2.5 text-xs",
        lg:      "h-10 px-5",
        icon:    "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
